import { Link } from 'react-router-dom';
import { useSEO } from '../../utils/seo';

export default function ServicesPage() {
  useSEO({
    title: 'Solar Panel Cleaning, Window Washing & Bird Proofing Sacramento | Mr. EcoClean',
    description: 'Complete solar panel cleaning, window washing, and bird proofing services in Sacramento CA. Maximize solar efficiency, crystal clear windows, and protect your property. Eco-friendly methods, professional results. Get your free quote today.',
    keywords: 'solar panel cleaning Sacramento, window washing Sacramento, bird proofing Sacramento, solar maintenance, residential window cleaning, commercial window washing, pigeon control, solar bird deterrent',
    canonical: '/services',
    ogType: 'website',
    schema: {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "Mr. EcoClean Services",
      "description": "Professional solar panel cleaning, window washing, and bird proofing services offered in Sacramento CA",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "item": {
            "@type": "Service",
            "name": "Solar Panel Cleaning",
            "description": "Professional solar panel cleaning using deionized water to maximize energy efficiency and extend panel lifespan",
            "provider": {
              "@type": "LocalBusiness",
              "name": "Mr. EcoClean",
              "telephone": "+19168366145"
            },
            "areaServed": {
              "@type": "City",
              "name": "Sacramento",
              "containedInPlace": {
                "@type": "State",
                "name": "California"
              }
            }
          }
        },
        {
          "@type": "ListItem",
          "position": 2,
          "item": {
            "@type": "Service",
            "name": "Window Washing",
            "description": "Professional residential and commercial window cleaning for crystal clear, streak-free results",
            "provider": {
              "@type": "LocalBusiness",
              "name": "Mr. EcoClean",
              "telephone": "+19168366145"
            },
            "areaServed": {
              "@type": "City",
              "name": "Sacramento",
              "containedInPlace": {
                "@type": "State",
                "name": "California"
              }
            }
          }
        },
        {
          "@type": "ListItem",
          "position": 3,
          "item": {
            "@type": "Service",
            "name": "Bird Proofing",
            "description": "Professional bird control and proofing solutions to protect solar panels and buildings from bird damage",
            "provider": {
              "@type": "LocalBusiness",
              "name": "Mr. EcoClean",
              "telephone": "+19168366145"
            },
            "areaServed": {
              "@type": "City",
              "name": "Sacramento",
              "containedInPlace": {
                "@type": "State",
                "name": "California"
              }
            }
          }
        }
      ]
    }
  });

  const services = [
    {
      title: "Solar Panel Cleaning",
      description: "Professional solar panel cleaning to maximize energy efficiency and extend panel lifespan",
      features: ["Deionized water cleaning", "No harsh chemicals", "Efficiency optimization", "Safety certified"],
      image: "https://public.readdy.ai/ai/img_res/1e4f0162b75952415b8617dbd95b7653.jpg",
      mapLink: "https://maps.app.goo.gl/tD952oeiaBgSo1HV9"
    },
    {
      title: "Residential Window Washing",
      description: "Crystal clear window cleaning for homes with professional streak-free results",
      features: ["Interior & exterior", "Streak-free shine", "Screen cleaning", "Track cleaning"],
      image: "https://readdy.ai/api/search-image?query=Professional%20window%20washing%20service%20cleaning%20residential%20home%20windows%2C%20technician%20using%20squeegee%20and%20professional%20equipment%20for%20streak-free%20crystal%20clear%20windows%2C%20beautiful%20two-story%20home%20with%20sparkling%20clean%20windows%2C%20bright%20sunny%20day%20in%20Sacramento%20California%20suburban%20neighborhood&width=400&height=300&seq=residential-window-washing&orientation=landscape"
    },
    {
      title: "Commercial Window Washing",
      description: "Professional window cleaning for businesses and commercial properties",
      features: ["High-rise capable", "Scheduled maintenance", "Storefront cleaning", "Office buildings"],
      image: "https://readdy.ai/api/search-image?query=Professional%20commercial%20window%20washing%20service%20on%20modern%20office%20building%2C%20technician%20cleaning%20large%20commercial%20windows%20with%20professional%20equipment%2C%20sparkling%20clean%20storefront%20windows%2C%20Sacramento%20business%20district%20with%20pristine%20commercial%20building%20exterior&width=400&height=300&seq=commercial-window-washing&orientation=landscape"
    },
    {
      title: "Solar Panel Bird Proofing",
      description: "Protect your solar panels from bird nesting and damage with professional bird proofing",
      features: ["Mesh installation", "Prevents nesting", "Protects wiring", "Long-lasting solution"],
      image: "https://readdy.ai/api/search-image?query=Professional%20bird%20proofing%20installation%20on%20residential%20solar%20panels%2C%20protective%20mesh%20barrier%20installed%20around%20solar%20panel%20edges%20to%20prevent%20bird%20nesting%2C%20clean%20solar%20installation%20on%20Sacramento%20home%20rooftop%2C%20effective%20pigeon%20deterrent%20system&width=400&height=300&seq=solar-bird-proofing&orientation=landscape"
    },
    {
      title: "Building Bird Control",
      description: "Comprehensive bird control solutions for commercial and residential buildings",
      features: ["Spike installation", "Netting systems", "Humane deterrents", "Cleanup included"],
      image: "https://readdy.ai/api/search-image?query=Professional%20bird%20control%20installation%20on%20commercial%20building%2C%20bird%20spikes%20and%20deterrent%20systems%20installed%20on%20building%20ledges%20and%20rooflines%2C%20effective%20pigeon%20control%20solution%2C%20clean%20Sacramento%20commercial%20property%20protected%20from%20bird%20damage&width=400&height=300&seq=building-bird-control&orientation=landscape"
    },
    {
      title: "Solar Panel Maintenance",
      description: "Complete solar panel maintenance including cleaning and inspection services",
      features: ["Performance check", "Panel inspection", "Efficiency testing", "Regular maintenance"],
      image: "https://readdy.ai/api/search-image?query=Professional%20solar%20panel%20maintenance%20and%20inspection%20service%2C%20technician%20checking%20solar%20panel%20performance%20and%20cleaning%20panels%20on%20residential%20rooftop%2C%20testing%20equipment%20and%20cleaning%20tools%2C%20Sacramento%20home%20with%20well-maintained%20solar%20installation&width=400&height=300&seq=solar-maintenance&orientation=landscape"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <nav className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <Link to="/" className="cursor-pointer">
                <h1 className="text-2xl font-bold text-red-600" style={{ fontFamily: '"Pacifico", serif' }}>
                  Mr. EcoClean
                </h1>
              </Link>
            </div>
            <div className="hidden md:flex space-x-8">
              <Link to="/" className="text-gray-700 hover:text-red-600 font-medium whitespace-nowrap cursor-pointer">Home</Link>
              <Link to="/services" className="text-red-600 font-medium whitespace-nowrap cursor-pointer">Services</Link>
              <Link to="/areas-covered" className="text-gray-700 hover:text-red-600 font-medium whitespace-nowrap cursor-pointer">Areas Covered</Link>
              <Link to="/about" className="text-gray-700 hover:text-red-600 font-medium whitespace-nowrap cursor-pointer">About</Link>
              <Link to="/contact" className="text-gray-700 hover:text-red-600 font-medium whitespace-nowrap cursor-pointer">Contact</Link>
            </div>
            <div className="md:hidden">
              <button className="text-gray-700 hover:text-red-600 cursor-pointer">
                <i className="ri-menu-line text-xl"></i>
              </button>
            </div>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="bg-red-600 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-5xl font-bold text-white mb-6">Our Professional Services</h2>
          <p className="text-xl text-red-100 max-w-3xl mx-auto">
            Solar panel cleaning, window washing, and bird proofing solutions for residential and commercial properties in Sacramento and surrounding areas.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img 
                  src={service.image}
                  alt={service.title}
                  className="w-full h-64 object-cover object-top"
                />
                <div className="p-8">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-2xl font-bold text-gray-900">{service.title}</h3>
                    {service.mapLink && (
                      <a 
                        href={service.mapLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sky-600 hover:text-sky-700 cursor-pointer"
                      >
                        <i className="ri-map-pin-line text-xl"></i>
                      </a>
                    )}
                  </div>
                  <p className="text-gray-600 mb-6">{service.description}</p>
                  <div className="grid grid-cols-2 gap-2 mb-6">
                    {service.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center">
                        <i className="ri-check-line text-sky-600 mr-2"></i>
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <Link 
                    to="/contact" 
                    className="bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors inline-block whitespace-nowrap cursor-pointer"
                  >
                    Get Quote
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">Our Process</h3>
            <p className="text-xl text-gray-600">
              Simple, professional, and effective service process
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-sky-600">1</span>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">Free Estimate</h4>
              <p className="text-gray-600">
                Contact us for a free, no-obligation estimate for your service needs.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-sky-600">2</span>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">Schedule Service</h4>
              <p className="text-gray-600">
                Choose a convenient time that works with your schedule for the service.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-sky-600">3</span>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">Professional Service</h4>
              <p className="text-gray-600">
                Our experienced team arrives with professional equipment and eco-friendly solutions.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-sky-600">4</span>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">Quality Inspection</h4>
              <p className="text-gray-600">
                We inspect our work to ensure you're completely satisfied with the results.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h3>
            <p className="text-xl text-gray-600">
              Common questions about our services
            </p>
          </div>
          
          <div className="space-y-6">
            <div className="bg-white rounded-lg p-6 shadow-md">
              <h4 className="text-xl font-semibold text-gray-900 mb-3">How often should I have my solar panels cleaned?</h4>
              <p className="text-gray-600">
                We recommend having your solar panels cleaned 2-4 times per year to maintain optimal efficiency. However, this can vary based on your location, weather conditions, and surrounding environment. Regular cleaning can improve energy production by 15-25%.
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow-md">
              <h4 className="text-xl font-semibold text-gray-900 mb-3">Is bird proofing safe for my solar panels?</h4>
              <p className="text-gray-600">
                Yes! Our bird proofing solutions are specifically designed for solar panels and will not damage your system. We use high-quality mesh that allows proper airflow while preventing birds from nesting underneath the panels.
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow-md">
              <h4 className="text-xl font-semibold text-gray-900 mb-3">Do you use eco-friendly cleaning products?</h4>
              <p className="text-gray-600">
                Yes! We're committed to using environmentally safe cleaning products and deionized water for solar panels. Our methods are effective yet gentle on your property and the environment.
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow-md">
              <h4 className="text-xl font-semibold text-gray-900 mb-3">How long does a typical service take?</h4>
              <p className="text-gray-600">
                The time varies depending on the size and scope of the project. Solar panel cleaning typically takes 1-3 hours, window washing takes 2-4 hours for an average home, and bird proofing installation takes 2-4 hours. We'll provide an estimated timeframe with your quote.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-red-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-4xl font-bold text-white mb-6">Ready to Get Started?</h3>
          <p className="text-xl text-red-100 mb-8">
            Contact us today for a free estimate on any of our services.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="bg-white text-red-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Free Quote
            </Link>
            <a 
              href="tel:916-836-6145"
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-red-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Call: (916) 836-6145
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h4 className="text-2xl font-bold text-sky-400 mb-4" style={{ fontFamily: '"Pacifico", serif' }}>
                Mr. EcoClean
              </h4>
              <p className="text-gray-300 mb-4">
                Professional solar panel cleaning, window washing, and bird proofing services for Sacramento and surrounding areas.
              </p>
              <div className="flex space-x-4">
                <i className="ri-facebook-fill text-xl text-gray-400 hover:text-sky-400 cursor-pointer"></i>
                <i className="ri-instagram-line text-xl text-gray-400 hover:text-sky-400 cursor-pointer"></i>
                <i className="ri-google-fill text-xl text-gray-400 hover:text-sky-400 cursor-pointer"></i>
              </div>
            </div>
            
            <div>
              <h5 className="font-semibold mb-4">Services</h5>
              <ul className="space-y-2 text-gray-300">
                <li><Link to="/services" className="hover:text-sky-400 cursor-pointer">Solar Panel Cleaning</Link></li>
                <li><Link to="/services" className="hover:text-sky-400 cursor-pointer">Window Washing</Link></li>
                <li><Link to="/services" className="hover:text-sky-400 cursor-pointer">Bird Proofing</Link></li>
              </ul>
            </div>
            
            <div>
              <h5 className="font-semibold mb-4">Contact Info</h5>
              <div className="space-y-2 text-gray-300">
                <p className="flex items-center">
                  <i className="ri-phone-line mr-2"></i>
                  <a href="tel:916-836-6145" className="hover:text-sky-400 cursor-pointer">(916) 836-6145</a>
                </p>
                <p className="flex items-center">
                  <i className="ri-mail-line mr-2"></i>
                  <a href="mailto:Jason@mrecoclean.com" className="hover:text-sky-400 cursor-pointer">Jason@mrecoclean.com</a>
                </p>
                <p className="flex items-center">
                  <i className="ri-map-pin-line mr-2"></i>
                  Sacramento, CA
                </p>
              </div>
            </div>
            
            <div>
              <h5 className="font-semibold mb-4">Business Hours</h5>
              <div className="space-y-2 text-gray-300">
                <p>Monday - Friday: 7:00 AM - 6:00 PM</p>
                <p>Saturday: 8:00 AM - 4:00 PM</p>
                <p>Sunday: Closed</p>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Mr. EcoClean. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-sky-400 cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
